import React, { useMemo, useState } from "react";
import {
  Collapse,
  Input,
  Card,
  Button,
  Tag,
} from "antd";
import {
  SearchOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  UserOutlined,
  ToolOutlined,
  MailOutlined,
} from "@ant-design/icons";

import "./nujobsEmployerHelp.scss";

const { Panel } = Collapse;

function NuJobsEmployerHelp() {
  const [searchText, setSearchText] = useState("");

  const helpSections = [
    {
      key: "getting-started",
      title: "Getting Started",
      description: "Account registration, approval and profile setup.",
      icon: <RocketOutlined />,
      questions: [
        {
          question: "How do I create an employer account?",
          answer:
            "Use the Employer Sign Up page to register your company. Enter your official company email address, company information and other required details. After registration, your account may require verification and approval before you can start using NuJobs.",
        },
        {
          question: "Why does my account require approval?",
          answer:
            "Employer accounts are reviewed to help ensure that jobs published on NuJobs are posted by genuine employers and authorized recruiters.",
        },
        {
          question: "What happens after my account is approved?",
          answer:
            "Once your account is approved, you can sign in to the NuJobs Employer Portal and access the features available under your subscription plan.",
        },
        {
          question: "How do I update my company information?",
          answer:
            "You can update the available company and account information from your employer profile or account settings.",
        },
      ],
    },

    {
      key: "subscription",
      title: "Subscription & Plans",
      description: "Understand your subscription, limits and renewals.",
      icon: <CreditCardOutlined />,
      questions: [
        {
          question: "What does Subscription Active mean?",
          answer:
            "Subscription Active means your current NuJobs subscription is valid. You can use the employer features and limits available under your active plan.",
        },
        {
          question: "What does Subscription Inactive mean?",
          answer:
            "Subscription Inactive means that your subscription is not currently active. This may happen when a plan has expired, has not yet been activated, or requires renewal.",
        },
        {
          question: "What happens when my subscription expires?",
          answer:
            "Some employer features may become unavailable after your subscription expires. You can go to the Renew / Buy Plan page to select a new subscription.",
        },
        {
          question: "How do I renew my subscription?",
          answer:
            "Open the Renew / Buy Plan page, select the subscription duration and required options, review your billing information and submit your renewal request.",
        },
        {
          question: "How many users can I have?",
          answer:
            "The number of users available depends on your selected subscription plan and any additional users included with your subscription.",
        },
        {
          question: "How many jobs can I post?",
          answer:
            "Your job posting limit depends on your current subscription plan and any additional job postings purchased with the plan.",
        },
        {
          question: "Where can I see my subscription history?",
          answer:
            "Your previous and current subscription information can be viewed from the Subscription History section in the employer portal.",
        },
      ],
    },

    {
      key: "payments",
      title: "Payments & Activation",
      description: "Payment instructions, confirmation and activation.",
      icon: <DollarOutlined />,
      questions: [
        {
          question: "How do I make a subscription payment?",
          answer:
            "After selecting your subscription, payment instructions will be sent to your registered email address. Follow the instructions provided to complete the payment.",
        },
        {
          question: "What happens after I make the payment?",
          answer:
            "After completing the payment, follow the payment confirmation instructions provided by NuJobs. Your payment will be verified before the subscription is activated.",
        },
        {
          question: "When will my subscription be activated?",
          answer:
            "Your subscription will be activated after the payment has been received and verified by the NuJobs team.",
        },
        {
          question:
            "My payment is completed but my subscription is still inactive. What should I do?",
          answer:
            "Payment verification may still be pending. If the subscription remains inactive after confirmation, contact NuJobs Support with your registered email address and payment reference.",
        },
      ],
    },

    {
      key: "jobs",
      title: "Job Posting",
      description: "Create, manage and track your job postings.",
      icon: <FileTextOutlined />,
      questions: [
        {
          question: "How do I post a new job?",
          answer:
            "Click Post a Job from your Employer Dashboard. Enter the job details, requirements, skills, experience and location information, then submit the job.",
        },
        {
          question: "Can I edit a posted job?",
          answer:
            "Yes. Open the relevant job from your job listings and use the available edit option to update the job information.",
        },
        {
          question: "What do Open, Hold and Closed mean?",
          answer:
            "Open means the job is currently accepting applications. Hold means hiring for the job has been temporarily paused. Closed means the job is no longer accepting applications.",
        },
        {
          question: "Why can't I post another job?",
          answer:
            "You may have reached the job posting limit available under your subscription. Check your current plan and available job posting balance.",
        },
      ],
    },

    {
      key: "applications",
      title: "Applications",
      description: "View and manage candidates who applied to your jobs.",
      icon: <TeamOutlined />,
      questions: [
        {
          question: "Where can I see applications?",
          answer:
            "Applications received for your jobs are available from the Applications section of the NuJobs Employer Portal.",
        },
        {
          question: "How do I view a candidate's resume?",
          answer:
            "Open the Applications section and select the candidate or application you want to review. Candidate information and the submitted resume will be available based on your access permissions.",
        },
        {
          question: "Can a candidate apply to the same job multiple times?",
          answer:
            "No. A candidate can apply to the same NuJobs job only once.",
        },
      ],
    },

    {
      key: "account",
      title: "Users & Account",
      description: "Manage recruiter accounts and account access.",
      icon: <UserOutlined />,
      questions: [
        {
          question: "How do I add another recruiter?",
          answer:
            "If your subscription allows additional users, you can add recruiter accounts from the available user management section.",
        },
        {
          question: "What happens when I reach my user limit?",
          answer:
            "You will not be able to add another user after reaching your subscription's user limit. You may need to purchase an additional user or change your subscription.",
        },
        {
          question: "Can multiple recruiters manage jobs?",
          answer:
            "Yes. Multiple authorized users can work within the employer account, subject to the users and permissions available under your subscription.",
        },
      ],
    },

    {
      key: "troubleshooting",
      title: "Troubleshooting",
      description: "Solutions for common NuJobs Employer issues.",
      icon: <ToolOutlined />,
      questions: [
        {
          question: "Why is my subscription showing Inactive?",
          answer:
            "Your subscription may have expired, may be awaiting activation, or payment verification may still be pending. Check your Subscription page for the current status.",
        },
        {
          question: "Why can't I add another user?",
          answer:
            "Check the maximum number of users allowed under your current subscription. If the limit has been reached, an additional user may need to be purchased.",
        },
        {
          question: "Why can't I post a new job?",
          answer:
            "Check that your subscription is active and that you still have job postings available under your current plan.",
        },
        {
          question: "I still need help. What should I do?",
          answer:
            "Contact NuJobs Support with your registered email address and a short description of the issue. Providing relevant job IDs or payment references can help us investigate the issue faster.",
        },
      ],
    },
  ];

  const filteredSections = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    if (!search) {
      return helpSections;
    }

    return helpSections
      .map((section) => {
        const questions = section.questions.filter(
          (item) =>
            item.question.toLowerCase().includes(search) ||
            item.answer.toLowerCase().includes(search)
        );

        return {
          ...section,
          questions,
        };
      })
      .filter((section) => section.questions.length > 0);
  }, [searchText]);

  return (
    <div className="nujobs-employer-help-wrapper">

      {/* Header */}
      <div className="help-header">
        <div>
          <h2>
            <QuestionCircleOutlined />
            Help & Support
          </h2>

          <p>
            Find answers about your NuJobs Employer account, subscription,
            job postings and applications.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="help-search-wrapper">
        <Input
          size="large"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search help topics..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* Subscription Status Help */}
      <Card className="subscription-help-card">
        <div className="subscription-help-title">
          Subscription Status
        </div>

        <div className="subscription-status-list">

          <div className="subscription-status-item">
            <Tag className="subscription-active-tag">
              Subscription Active
            </Tag>

            <span>
              Your subscription is active and you can use the features
              available under your current plan.
            </span>
          </div>

          <div className="subscription-status-item">
            <Tag className="subscription-inactive-tag">
              Subscription Inactive
            </Tag>

            <span>
              Your subscription has expired, is awaiting activation,
              or requires renewal.
            </span>
          </div>

        </div>
      </Card>

      {/* FAQ Sections */}
      <div className="help-sections">

        {filteredSections.map((section) => (
          <Card
            key={section.key}
            className="help-section-card"
          >
            <div className="help-section-header">

              <div className="help-section-icon">
                {section.icon}
              </div>

              <div>
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </div>

            </div>

            <Collapse
              ghost
              expandIconPosition="end"
              className="help-collapse"
            >
              {section.questions.map((item, index) => (
                <Panel
                  header={item.question}
                  key={`${section.key}-${index}`}
                >
                  <p>{item.answer}</p>
                </Panel>
              ))}
            </Collapse>

          </Card>
        ))}

        {filteredSections.length === 0 && (
          <div className="help-no-results">
            <QuestionCircleOutlined />

            <h3>No help topics found</h3>

            <p>
              Try searching with different keywords.
            </p>
          </div>
        )}

      </div>

      {/* Contact Support */}
      <Card className="help-support-card">

        <div className="help-support-content">

          <div className="help-support-icon">
            <MailOutlined />
          </div>

          <div className="help-support-text">
            <h3>Still need help?</h3>

            <p>
              If you couldn't find the answer you're looking for,
              contact the NuJobs Support team Whatsapp: +919591270077, Email: "support@nujobs.com"
            </p>
          </div>

        </div>

      </Card>

    </div>
  );
}

export default NuJobsEmployerHelp;